import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { parseCurrency } from '@utils/currency.util';
import { Alert, Form, Input, Modal, Radio, Space, Typography } from 'antd';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';

import {
  formatBRLFromCents,
  formatInputCurrency,
  formatPercentageInput,
  parsePercentageToBasisPoints,
} from './helpers';
import {
  CalculationTypeGroup,
  CurrentValue,
  InstallmentCount,
  InstallmentGrid,
  InstallmentItem,
  InstallmentValue,
  OperationCard,
  OperationCopy,
  OperationDescription,
  OperationGroup,
  SummaryCard,
  SummaryEquation,
  SummaryLabel,
  SummaryValue,
} from './styles';

export type PriceCalculationOperation = 'discount' | 'increase';
export type PriceCalculationType = 'percentage' | 'fixed';

const MINIMUM_INSTALLMENT_VALUE_IN_CENTS = 1990;

interface PriceCalculatorModalProps {
  open: boolean;
  fieldLabel: string;
  baseValue: string;
  onApply: (value: string) => void;
  onClose: () => void;
}

export const PriceCalculatorModal: FC<PriceCalculatorModalProps> = ({
  open,
  fieldLabel,
  baseValue,
  onApply,
  onClose,
}) => {
  const [operation, setOperation] =
    useState<PriceCalculationOperation>('discount');
  const [calculationType, setCalculationType] =
    useState<PriceCalculationType>('percentage');
  const [calculationValue, setCalculationValue] = useState('');
  const [hasInvalidCharacters, setHasInvalidCharacters] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    if (open) {
      setOperation('discount');
      setCalculationType('percentage');
      setCalculationValue('');
      setHasInvalidCharacters(false);
      setHasInteracted(false);
    }
  }, [open, baseValue]);

  const baseValueInCents = Math.round((parseCurrency(baseValue) ?? 0) * 100);
  const percentageInBasisPoints =
    calculationType === 'percentage'
      ? parsePercentageToBasisPoints(calculationValue)
      : 0;
  const fixedValueInCents =
    calculationType === 'fixed'
      ? Math.round((parseCurrency(calculationValue) ?? 0) * 100)
      : 0;
  const calculationValueInCents =
    calculationType === 'percentage'
      ? Math.round((baseValueInCents * percentageInBasisPoints) / 10000)
      : fixedValueInCents;
  const resultInCents =
    operation === 'discount'
      ? baseValueInCents - calculationValueInCents
      : baseValueInCents + calculationValueInCents;

  const validationMessage = useMemo(() => {
    if (hasInvalidCharacters) {
      return 'Informe apenas números para realizar o cálculo.';
    }
    if (
      !calculationValue ||
      (calculationType === 'percentage'
        ? percentageInBasisPoints <= 0
        : fixedValueInCents <= 0)
    ) {
      return 'Informe um valor maior que zero.';
    }
    if (resultInCents <= 0) {
      return 'O resultado final deve ser maior que zero.';
    }
    return undefined;
  }, [
    calculationValue,
    calculationType,
    fixedValueInCents,
    hasInvalidCharacters,
    percentageInBasisPoints,
    resultInCents,
  ]);

  const installments = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const count = index + 1;
        return {
          count,
          valueInCents: Math.round(Math.max(resultInCents, 0) / count),
        };
      }),
    [resultInCents]
  );

  const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setHasInteracted(true);
    setHasInvalidCharacters(/[^0-9.,\s]/.test(inputValue));
    setCalculationValue(
      calculationType === 'percentage'
        ? formatPercentageInput(inputValue)
        : formatInputCurrency(inputValue)
    );
  };

  const handleCalculationTypeChange = (type: PriceCalculationType) => {
    setCalculationType(type);
    setCalculationValue('');
    setHasInvalidCharacters(false);
    setHasInteracted(false);
  };

  const handleApply = () => {
    if (validationMessage) return;
    onApply((resultInCents / 100).toFixed(2));
  };

  const operationSymbol = operation === 'discount' ? '−' : '+';
  const actionLabel =
    operation === 'discount' ? 'Aplicar desconto' : 'Aplicar acréscimo';
  const calculationDisplay =
    calculationType === 'percentage'
      ? `${calculationValue || '0'}%`
      : formatBRLFromCents(calculationValueInCents);

  return (
    <Modal
      title={`Calcular ${fieldLabel}`}
      open={open}
      onCancel={onClose}
      onOk={handleApply}
      okText={actionLabel}
      cancelText='Cancelar'
      okButtonProps={{ disabled: Boolean(validationMessage) }}
      destroyOnClose
      width={560}
    >
      <Space direction='vertical' size={20} style={{ width: '100%' }}>
        <CurrentValue>
          Valor atual: {formatBRLFromCents(baseValueInCents)}
        </CurrentValue>

        <div>
          <Typography.Text strong>Tipo de cálculo</Typography.Text>
          <CalculationTypeGroup
            value={calculationType}
            onChange={event => handleCalculationTypeChange(event.target.value)}
            style={{ marginTop: 8 }}
          >
            <Radio.Button value='percentage'>Porcentagem</Radio.Button>
            <Radio.Button value='fixed'>Valor fixo</Radio.Button>
          </CalculationTypeGroup>
        </div>

        <Form.Item
          label={'Valor para cálculo'}
          validateStatus={
            hasInteracted && validationMessage ? 'error' : undefined
          }
          help={hasInteracted ? validationMessage : undefined}
          style={{ marginBottom: 0 }}
        >
          <Input
            aria-label='Valor para cálculo'
            autoFocus
            inputMode='decimal'
            maxLength={18}
            placeholder={calculationType === 'percentage' ? '0' : '0,00'}
            prefix={calculationType === 'percentage' ? undefined : 'R$'}
            suffix={calculationType === 'percentage' ? '%' : undefined}
            value={calculationValue}
            onChange={handleValueChange}
          />
        </Form.Item>

        <div>
          <Typography.Text strong>Tipo de operação</Typography.Text>
          <OperationGroup
            value={operation}
            onChange={event => setOperation(event.target.value)}
            style={{ marginTop: 8 }}
          >
            <OperationCard $selected={operation === 'discount'}>
              <Radio value='discount' />
              <ArrowDownOutlined />
              <OperationCopy>
                <Typography.Text strong>Desconto</Typography.Text>
                <OperationDescription>
                  Reduzir do valor atual
                </OperationDescription>
              </OperationCopy>
            </OperationCard>
            <OperationCard $selected={operation === 'increase'}>
              <Radio value='increase' />
              <ArrowUpOutlined />
              <OperationCopy>
                <Typography.Text strong>Acréscimo</Typography.Text>
                <OperationDescription>
                  Adicionar ao valor atual
                </OperationDescription>
              </OperationCopy>
            </OperationCard>
          </OperationGroup>
        </div>

        <SummaryCard size='small' title='Resumo do cálculo'>
          <SummaryEquation>
            <div>
              <SummaryValue>
                {formatBRLFromCents(baseValueInCents)}
              </SummaryValue>
              <SummaryLabel>Valor atual</SummaryLabel>
            </div>
            <Typography.Text>{operationSymbol}</Typography.Text>
            <div>
              <SummaryValue>{calculationDisplay}</SummaryValue>
              <SummaryLabel>
                {operation === 'discount' ? 'Desconto' : 'Acréscimo'}
              </SummaryLabel>
            </div>
            <Typography.Text>=</Typography.Text>
            <div>
              <SummaryValue $highlight={resultInCents > 0}>
                {resultInCents > 0 ? formatBRLFromCents(resultInCents) : '—'}
              </SummaryValue>
              <SummaryLabel $highlight={resultInCents > 0}>
                Novo valor
              </SummaryLabel>
            </div>
          </SummaryEquation>
        </SummaryCard>

        <div>
          <Typography.Text strong>
            Quanto ficaria para cada parcelamento
          </Typography.Text>
          <Typography.Text
            type='secondary'
            style={{ display: 'block', fontSize: 12, marginTop: 2 }}
          >
            Parcelas abaixo de R$ 19,90 são destacadas apenas como alerta.
          </Typography.Text>
          <InstallmentGrid style={{ marginTop: 8 }}>
            {installments.map(installment => {
              const isBelowMinimum =
                resultInCents > 0 &&
                installment.valueInCents < MINIMUM_INSTALLMENT_VALUE_IN_CENTS;

              return (
                <InstallmentItem
                  key={installment.count}
                  $warning={isBelowMinimum}
                  title={
                    isBelowMinimum
                      ? 'Valor da parcela abaixo de R$ 19,90'
                      : undefined
                  }
                >
                  <InstallmentCount $warning={isBelowMinimum}>
                    {installment.count}x
                  </InstallmentCount>
                  <InstallmentValue>
                    {resultInCents > 0
                      ? formatBRLFromCents(installment.valueInCents)
                      : '—'}
                  </InstallmentValue>
                </InstallmentItem>
              );
            })}
          </InstallmentGrid>
        </div>

        {!calculationValue && (
          <Alert
            icon={<InfoCircleOutlined />}
            message='O campo será atualizado somente após confirmar a operação.'
            type='info'
            showIcon
          />
        )}
      </Space>
    </Modal>
  );
};
